import { ServiceInput } from '@/__generated__/graphql';
import { ES_MAPPING_KEYS, esSheetColumns, serviceKeys } from '@/components/EsSheet/data';
import { EsSheetData } from './UploadPanel';
import { calculationKeys } from '@/components/Analysis/RiskAssessment/constants';

export const computeFinalData = (esSheet: EsSheetData): string[][] => {
  const {
    headers,
    data: sheetData,
    mappings,
    valueMappings,
    inlineEdits,
    excludedRows = [],
  } = esSheet;

  // Apply inline edits to data first
  const dataWithEdits = sheetData.map((row, rowIndex) => {
    const newRow = [...row];
    if (inlineEdits) {
      headers.forEach((_, colIndex) => {
        const editKey = `${rowIndex}:${colIndex}`;
        if (inlineEdits[editKey] !== undefined) {
          newRow[colIndex] = inlineEdits[editKey];
        }
      });
    }
    return newRow;
  });

  // Apply value mappings if they exist
  const dataWithValueMappings = dataWithEdits.map((row) => {
    return row.map((cellValue, colIndex) => {
      const header = headers[colIndex];
      const targetKey = mappings?.[header];
      const systemField = calculationKeys.find((k) => k.key === targetKey);
      const mappedColumnName = systemField?.name || header;

      if (valueMappings?.[mappedColumnName]?.[cellValue]) {
        const mappedValue = valueMappings[mappedColumnName][cellValue];
        return mappedValue === '__RETAIN__' ? cellValue : mappedValue;
      }
      return cellValue;
    });
  });

  // Filter out excluded rows
  return dataWithValueMappings.filter((_, index) => !excludedRows.includes(index));
};

export const processEsSheetData = (esSheet: EsSheetData, isLegacy: boolean) => {
  const { headers } = esSheet;

  // Get final data with all transformations applied
  const includedData = computeFinalData(esSheet);

  // Create a mapping from header name to key
  const headerMapping = isLegacy
    ? ES_MAPPING_KEYS.reduce((acc, mapping) => {
        acc[mapping.name] = mapping.key;
        return acc;
      }, {} as Record<string, string | undefined>)
    : Object.entries(esSheetColumns).reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string | undefined>);

  return includedData.map((row) => {
    const templateFieldData = {} as Record<string, string>;
    const serviceObject = {} as Record<string, string>;

    headers.forEach((header, index) => {
      const mappedKey = headerMapping[header];

      if (!mappedKey) {
        // Skip unmapped headers
        return;
      }

      if (serviceKeys.has(mappedKey)) {
        serviceObject[mappedKey] = row[index];
      } else {
        templateFieldData[mappedKey] = row[index];
      }
    });

    const service: ServiceInput = {
      group: serviceObject?.group ?? 'Unknown Services',
      ...(serviceObject?.code ? { code: serviceObject.code } : {}),
    };

    return {
      service,
      tagName: row[0],
      templateFieldData: JSON.stringify(templateFieldData),
    };
  });
};
